import { useState } from "react";
import { ArrowLeft, Bell, BellOff, Search, Grid2x2, List, ArrowUpDown, UserPlus, Send, ChevronDown } from "lucide-react";

type Status = "critical" | "warning" | "normal" | "noscan";

type PatientAnalysis = {
  id: string;
  ref: string;
  status: Status;
  marker: string; // headline analyte
  value: string;
  unit: string;
  bars: number[]; // 14 bars 0..1
  updated: string;
  metrics: { label: string; value: string; tone?: "danger" | "warning" | "normal" }[];
  alerts: number;
};

// 22-sample sparkline generator. `profile` adds a late-window slope so a
// "spike" patient visually trends up at the right edge and a "decline"
// patient gently drops — the values themselves stay in [0.1, 1].
const seedBars = (seed: number, profile: "spike" | "stable" | "decline" = "stable") =>
  Array.from({ length: 22 }, (_, i) => {
    const base = Math.sin(i / 1.8 + seed) * 0.25 + 0.55;
    const noise = Math.cos(i / 0.9 + seed * 2) * 0.15;
    const trend = profile === "spike" ? Math.max(0, (i - 14) / 8) : profile === "decline" ? -i / 60 : 0;
    return Math.min(1, Math.max(0.1, base + noise + trend));
  });


const PATIENTS: PatientAnalysis[] = [
  {
    id: "440363",
    ref: "2007B",
    status: "critical",
    marker: "Glycémie",
    value: "2.38",
    unit: "g/L",
    bars: seedBars(1, "spike"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c (%)", value: "8.2", tone: "danger" },
      { label: "Chol. (g/L)", value: "2.71" },
      { label: "HDL", value: "0.38", tone: "warning" },
      { label: "CRP", value: "12", tone: "danger" },
    ],
    alerts: 3,
  },
  {
    id: "440267",
    ref: "2007C",
    status: "critical",
    marker: "Glycémie",
    value: "2.38",
    unit: "g/L",
    bars: seedBars(2, "spike"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "7.4", tone: "warning" },
      { label: "Chol.", value: "2.10" },
      { label: "HDL", value: "0.60" },
      { label: "CRP", value: "9", tone: "warning" },
    ],
    alerts: 24,
  },
  {
    id: "440263",
    ref: "2005",
    status: "critical",
    marker: "Glycémie",
    value: "2.34",
    unit: "g/L",
    bars: seedBars(3, "spike"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "8.0", tone: "danger" },
      { label: "Chol.", value: "2.80", tone: "warning" },
      { label: "HDL", value: "0.41", tone: "warning" },
      { label: "CRP", value: "5" },
    ],
    alerts: 2,
  },
  {
    id: "440410",
    ref: "2005A",
    status: "critical",
    marker: "Glycémie",
    value: "2.34",
    unit: "g/L",
    bars: seedBars(4, "spike"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "7.9", tone: "danger" },
      { label: "Chol.", value: "1.30/0.85" },
      { label: "HDL", value: "0.55" },
      { label: "CRP", value: "9", tone: "warning" },
    ],
    alerts: 14,
  },
  {
    id: "440271",
    ref: "2010A",
    status: "warning",
    marker: "Glycémie",
    value: "1.26",
    unit: "g/L",
    bars: seedBars(5, "stable"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "6.2" },
      { label: "Chol.", value: "1.95" },
      { label: "HDL", value: "0.62" },
      { label: "CRP", value: "5" },
    ],
    alerts: 0,
  },
  {
    id: "440249",
    ref: "2011",
    status: "warning",
    marker: "Glycémie",
    value: "1.21",
    unit: "g/L",
    bars: seedBars(6, "stable"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "6.0" },
      { label: "Chol.", value: "1.85" },
      { label: "HDL", value: "0.65" },
      { label: "CRP", value: "4" },
    ],
    alerts: 0,
  },
  {
    id: "440480",
    ref: "2002b",
    status: "noscan",
    marker: "Glycémie",
    value: "0.96",
    unit: "g/L",
    bars: seedBars(7, "stable"),
    updated: "30/10 15:40",
    metrics: [],
    alerts: 0,
  },
  {
    id: "440470",
    ref: "2004B",
    status: "critical",
    marker: "Glycémie",
    value: "0.96",
    unit: "g/L",
    bars: seedBars(8, "decline"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "8.4", tone: "danger" },
      { label: "Chol.", value: "1.30/0.85" },
      { label: "HDL", value: "0.45", tone: "warning" },
      { label: "CRP", value: "9", tone: "warning" },
    ],
    alerts: 14,
  },
  {
    id: "440471",
    ref: "CAR",
    status: "normal",
    marker: "Glycémie",
    value: "0.94",
    unit: "g/L",
    bars: seedBars(9, "stable"),
    updated: "30/10 15:40",
    metrics: [
      { label: "HbA1c", value: "5.4" },
      { label: "Chol.", value: "1.70" },
      { label: "HDL", value: "0.70" },
      { label: "CRP", value: "2" },
    ],
    alerts: 0,
  },
];

const STATUS_BORDER: Record<Status, string> = {
  critical: "border-destructive/70 shadow-[0_0_0_1px_oklch(0.65_0.22_27/0.4)_inset]",
  warning: "border-[oklch(0.78_0.16_80)]/60",
  normal: "border-white/10",
  noscan: "border-white/10 opacity-80",
};

const STATUS_TEXT: Record<Status, string> = {
  critical: "text-destructive",
  warning: "text-[oklch(0.85_0.18_85)]",
  normal: "text-success",
  noscan: "text-muted-foreground",
};

const BAR_COLOR: Record<Status, string> = {
  critical: "bg-destructive",
  warning: "bg-[oklch(0.85_0.18_85)]",
  normal: "bg-success",
  noscan: "bg-white/15",
};

function Histogram({ data, status }: { data: number[]; status: Status }) {
  return (
    <div className="flex h-12 items-end gap-[2px]">
      {data.map((v, i) => (
        <div
          key={i}
          className={`w-full rounded-sm ${BAR_COLOR[status]}`}
          style={{ height: `${Math.max(8, v * 100)}%`, opacity: 0.55 + v * 0.45 }}
        />
      ))}
    </div>
  );
}

function PatientCard({ p }: { p: PatientAnalysis }) {
  return (
    <div className={`group relative rounded-xl border bg-black/40 p-3 transition-all hover:bg-black/60 ${STATUS_BORDER[p.status]}`}>
      <div className="flex items-center justify-between text-xs">
        <div className="font-mono tracking-wider text-foreground/90">
          {p.id} <span className="text-muted-foreground">/ {p.ref}</span>
        </div>
        <button className="relative grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:text-foreground">
          <Bell className="h-3.5 w-3.5" />
          {p.alerts > 0 && (
            <span className="absolute -right-1 -top-1 grid h-3.5 min-w-[14px] place-items-center rounded-full bg-destructive px-1 text-[9px] font-medium text-destructive-foreground">
              {p.alerts}
            </span>
          )}
        </button>
      </div>

      <div className="mt-2 text-[10px] text-muted-foreground">{p.marker} ({p.unit})</div>

      {p.status === "noscan" ? (
        <div className="my-6 grid place-items-center text-sm text-muted-foreground">No scan</div>
      ) : (
        <div className="mt-1 flex items-end gap-3">
          <div className={`text-2xl font-light leading-none ${STATUS_TEXT[p.status]}`}>{p.value}</div>
          <div className="min-w-0 flex-1">
            <Histogram data={p.bars} status={p.status} />
          </div>
        </div>
      )}

      <div className="mt-2 text-[10px] text-muted-foreground">
        Dernière analyse, {p.updated}
      </div>

      {p.metrics.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-2 border-t border-white/5 pt-2">
          {p.metrics.map((m) => (
            <div key={m.label}>
              <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{m.label}</div>
              <div
                className={
                  "text-sm font-light " +
                  (m.tone === "danger"
                    ? "text-destructive"
                    : m.tone === "warning"
                      ? "text-[oklch(0.85_0.18_85)]"
                      : "text-foreground")
                }
              >
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnalysesGridView({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState<"grid" | "list">("grid");
  const [filter, setFilter] = useState<"ALL" | "ACTIVE">("ALL");
  const [muted, setMuted] = useState(false);
  const alerts = PATIENTS.filter((p) => p.alerts > 0);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="card-surface flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Retour
        </button>
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-light">Patients</h2>
          <span className="text-xs text-muted-foreground">({PATIENTS.length})</span>
        </div>

        <div className="relative ml-2 flex-1 min-w-[180px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Rechercher patient, analyte…"
            className="h-9 w-full rounded-full border border-white/10 bg-black/30 pl-9 pr-3 text-xs outline-none focus:border-primary/50"
          />
        </div>

        <button className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowUpDown className="h-3.5 w-3.5" /> Glycémie, décroissant <ChevronDown className="h-3 w-3" />
        </button>

        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs">
            {(["ALL", "ACTIVE"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={
                  "rounded-full px-3 py-1 " +
                  (filter === f ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")
                }
              >
                {f}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-white/10 p-0.5">
            <button
              onClick={() => setView("grid")}
              className={"grid h-7 w-7 place-items-center rounded-full " + (view === "grid" ? "bg-foreground text-background" : "text-muted-foreground")}
            >
              <Grid2x2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setView("list")}
              className={"grid h-7 w-7 place-items-center rounded-full " + (view === "list" ? "bg-foreground text-background" : "text-muted-foreground")}
            >
              <List className="h-3.5 w-3.5" />
            </button>
          </div>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
            <Send className="h-3.5 w-3.5" /> Transférer
          </button>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
            <UserPlus className="h-3.5 w-3.5" /> Ajouter patient
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        {PATIENTS.map((p) => (
          <PatientCard key={p.id} p={p} />
        ))}
      </div>

      {/* Alerts footer */}
      <div className="card-surface flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-destructive/20 text-destructive">!</span>
          <span>Patients avec alertes</span>
          <span className="text-muted-foreground">({alerts.length})</span>
          <button
            onClick={() => setMuted((m) => !m)}
            className="ml-1 grid h-7 w-7 place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-foreground"
            aria-label="Couper le son"
          >
            {muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {alerts.map((a) => (
            <button
              key={a.id}
              className="relative rounded-lg border border-white/10 bg-black/40 px-3 py-1.5 text-left text-[10px] font-mono"
            >
              <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-destructive px-1 text-[9px] text-destructive-foreground">
                {a.alerts}
              </span>
              <div>{a.id}</div>
              <div className="text-muted-foreground">{a.ref}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
